import React, { useEffect, useRef, useState } from 'react';
import './VersionReasonModal.scss';

interface VersionReasonModalProps {
  isOpen: boolean;
  title?: string;
  placeholder?: string;
  confirmBtnText?: string;
  cancelBtnText?: string;
  isView?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const VersionReasonModal: React.FC<VersionReasonModalProps> = ({
  isOpen,
  title = 'Reason for Creating New Version',
  placeholder = 'Enter your reason here...',
  confirmBtnText = 'Create Version',
  cancelBtnText = 'Cancel',
  isView = false,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  const isReasonValid = (): boolean => {
    return reason ? reason.trim().length > 0 : false;
  };

  const handleConfirm = (): void => {
    if (isReasonValid()) {
      onConfirm(reason);
      setReason('');
    }
  };

  const handleCancel = (): void => {
    setReason('');
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      if (isReasonValid()) {
        handleConfirm();
      }
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="version-reason-modal-overlay">
      <div className="version-reason-modal">
        <div className="modal-header">
          <h4 className="modal-title">{title}</h4>
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={handleCancel}
          >
            <span aria-hidden="true">
              <i className="las la-times la-lg" aria-hidden="true"></i>
            </span>
          </button>
        </div>

        <div className="modal-body form-margin">
          <div className="form-group">
            <label className="form-label">Reason</label>

            {!isView ? (
              <textarea
                ref={textareaRef}
                className="form-control"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                name="reason"
                placeholder={placeholder}
                onKeyDown={handleKeyDown}
              />
            ) : (
              <div
                className={`reason-text-display${reason ? '' : ' is-empty'}`}
              >
                {reason || ''}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCancel}
          >
            <i className="las la-times"></i>&nbsp;{cancelBtnText}
          </button>
          {!isView && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={!isReasonValid()}
            >
              <i className="las la-check"></i>&nbsp;{confirmBtnText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
